import React, {Fragment, useState} from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useTranslation} from 'react-i18next';
import {NodeType} from "crqlar-react-common/dist/models/automation/automation";
import {FLOATING_SHADOW} from '../../../../common/newVariablePicker/shared/floatingShadow';
import {getNodeColor, NodeCategory, NodeCategoryConfig} from '../../config/nodeCategories';
import {useNodeDragToCanvas} from '../../hooks/useNodeDragToCanvas';
import {NODE_COPY} from './nodeCopy';
import NodeDragPreview from './NodeDragPreview';
import NodeReferenceDialog from './NodeReferenceDialog';

interface NodeSelectorPopoverProps {
    anchorEl?: HTMLElement | null;
    /** Viewport coordinates for an anchor-less opening (double-click on the canvas). */
    anchorPosition?: {top: number; left: number} | null;
    onClose: () => void;
    nodeCategories: NodeCategory[];
    onAddNode: (nodeType: NodeType, label: string) => void;
    onDropNode?: (nodeType: NodeType, label: string, client: {x: number; y: number}) => void;
    title?: string;
}

const POPOVER_WIDTH = 360;

const NodeSelectorPopover: React.FC<NodeSelectorPopoverProps> = ({
    anchorEl,
    anchorPosition,
    onClose,
    nodeCategories,
    onAddNode,
    onDropNode,
    title,
}) => {
    const {t} = useTranslation();
    const [referenceOpen, setReferenceOpen] = useState(false);

    const draggable = Boolean(onDropNode);

    const {draggedItem, previewRef, getDragHandlers, consumeClickSuppression} =
        useNodeDragToCanvas<NodeCategoryConfig>((node, client) => {
            onDropNode?.(node.type, t(node.label), client);
        });

    return (
        <>
            <Popover
                open={Boolean(anchorEl) || Boolean(anchorPosition)}
                anchorEl={anchorEl}
                anchorReference={anchorEl ? 'anchorEl' : 'anchorPosition'}
                anchorPosition={anchorPosition ?? undefined}
                onClose={onClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                transformOrigin={{vertical: 'top', horizontal: 'left'}}
                disableScrollLock
                transitionDuration={0}
                slotProps={{
                    paper: {
                        variant: 'outlined',
                        elevation: 0,
                        sx: {
                            mt: 1,
                            width: POPOVER_WIDTH,
                            maxWidth: '100%',
                            maxHeight: 'min(480px, calc(100vh - 120px))',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            borderRadius: 2,
                            boxShadow: FLOATING_SHADOW,
                        },
                    },
                }}
            >
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    gap={1}
                    sx={{px: 2, py: 1.25, flexShrink: 0}}
                >
                    <Typography variant={'subtitle2'}>{title ?? t('Add node')}</Typography>

                    <Stack direction={'row'} alignItems={'center'} gap={0.5}>
                        <Tooltip title={t('Node reference')}>
                            <IconButton
                                size={'small'}
                                aria-label={t('Node reference')}
                                onClick={() => setReferenceOpen(true)}
                            >
                                <InfoOutlinedIcon fontSize={'small'}/>
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={t('Close')}>
                            <IconButton onClick={onClose} size={'small'} aria-label={t('Close')}>
                                <CloseIcon fontSize={'small'}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Divider/>

                <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
                    <List disablePadding>
                        {nodeCategories.map((category) => (
                            <Fragment key={category.name}>
                                <Stack
                                    component={'li'}
                                    direction={'row'}
                                    alignItems={'baseline'}
                                    gap={0.75}
                                    sx={{px: 2, pt: 1.5, pb: 0.5}}
                                >
                                    <Typography variant={'overline'} color={'text.secondary'}>
                                        {t(category.name)}
                                    </Typography>
                                    <Typography variant={'overline'} color={'text.disabled'}>
                                        {category.nodes.length}
                                    </Typography>
                                </Stack>

                                {category.nodes.map((node) => {
                                    const copy = NODE_COPY[node.type];
                                    const dragHandlers = draggable ? getDragHandlers(node) : undefined;

                                    return (
                                        <ListItem
                                            key={node.type}
                                            disablePadding
                                            sx={{'& .MuiListItemSecondaryAction-root': {pointerEvents: 'none'}}}
                                            secondaryAction={draggable ? (
                                                <DragIndicatorIcon
                                                    fontSize={'small'}
                                                    sx={{color: 'text.disabled', display: 'block'}}
                                                />
                                            ) : undefined}
                                        >
                                            <ListItemButton
                                                {...dragHandlers}
                                                onClick={() => {
                                                    if (consumeClickSuppression()) return;
                                                    onAddNode(node.type, t(node.label));
                                                }}
                                                sx={{
                                                    py: 1,
                                                    pr: draggable ? 6 : 2,
                                                    ...(dragHandlers ? {
                                                        cursor: 'grab',
                                                        '&:active': {cursor: 'grabbing'},
                                                    } : {}),
                                                }}
                                            >
                                                <ListItemIcon sx={{minWidth: 40, color: getNodeColor(node.type)}}>
                                                    {node.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={t(node.label)}
                                                    secondary={copy ? t(copy.summary) : undefined}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </List>
                </Box>
            </Popover>

            <NodeReferenceDialog
                open={referenceOpen}
                onClose={() => setReferenceOpen(false)}
                nodeCategories={nodeCategories}
            />

            {draggedItem && <NodeDragPreview node={draggedItem} previewRef={previewRef}/>}
        </>
    );
};

export default NodeSelectorPopover;
